/* J Hans & Sia collection page: facets, sort, colourway-family collapse.

   Port of the prototype's assets/watches.js, reading each watch off the data
   attributes that sections/jhs-collection.liquid puts on the card wrapper
   instead of off a WATCHES array. One behaviour per the prototype:

     - colourway families collapse to ONE card, the first surviving member in
       the current sort order
     - the count line reads "Showing N models · M watches" until something is
       filtered, then "Showing N of M models"
     - a facet with fewer than two values to choose between is removed, not
       shown empty
     - the rail is a sticky column at >=1024px and a bottom sheet below it

   The catalog arrives 50 products at a time. Page one is server-rendered; the
   rest are pulled through the Section Rendering API right after first paint, so
   filtering is whole-catalog even though the first byte is not. */

(function () {
  const root = document.querySelector('[data-jhs-collection]');
  if (!root) return;

  const $ = (sel, scope = root) => scope.querySelector(sel);
  const $$ = (sel, scope = root) => Array.from(scope.querySelectorAll(sel));

  const grid = $('[data-grid]');
  /* Products that become cards. The server sends the collection's count minus
     the grid-hidden products on the page it rendered (strap options that are not
     the watch's representative never become cards); hydrate() subtracts each
     fetched page's own hidden count as it arrives. A page that never arrives
     keeps its hidden products in this number, so a short grid still reads short. */
  let totalProducts = Number(root.dataset.totalProducts || 0);
  /* Set by checkCatalogueComplete() once hydration has run. Until then the grid
     is whatever the server rendered, which is complete by definition. */
  let catalogueComplete = true;

  /* ---------- facet vocabularies ----------
     Canonical orders. Colour matches the eight choices pinned on the
     specs.colour_group metafield definition; the hexes are filter-dot UI, not
     product data (a product's own dial colour is specs.dial_swatch, on the card). */
  const COLOURS = [
    ['Black', '#17181a'],
    ['White & Cream', '#ede6d6'],
    ['Blue', '#1f3a6b'],
    ['Green', '#2f5a3c'],
    ['Grey & Silver', '#c9cbcf'],
    ['Brown & Gold', '#7a4b32'],
    ['Red & Burgundy', '#5a2230'],
    ['Multicolour', null],
  ];
  const MOVEMENTS = ['Automatic', 'Manual wind', 'Quartz', 'Mecha quartz', 'Solar'];
  const STYLES = ['Dress', 'Field', 'Pilot', 'Diver', 'GADA', 'Chronograph'];
  const AVAILABILITY = ['In stock', 'Pre-order', 'Back-order', 'In store only', 'Coming soon', 'Sold out'];
  /* The facet values are the product's own state strings, but "Back-order" is
     trade jargon and must never be shown to a customer, in the filter rail or in
     an applied filter chip. The value stays the state, the label is the promise. */
  const AVAILABILITY_LABELS = { 'Back-order': 'Ships in 2 weeks' };

  const SIZE_BANDS = [
    { value: 'u36', label: 'Under 36 mm', test: (s) => s > 0 && s < 36 },
    { value: '36-39', label: '36 to 39 mm', test: (s) => s >= 36 && s < 40 },
    { value: '40-42', label: '40 to 42 mm', test: (s) => s >= 40 && s < 43 },
    { value: '43up', label: '43 mm and over', test: (s) => s >= 43 },
  ];

  /* Money labels come from Liquid so they follow the store's currency format. */
  const priceLabels = (root.dataset.priceBands || '').split('|').map((s) => s.trim());
  const PRICE_BANDS = [
    { value: 'u250', label: 'Under ' + (priceLabels[0] || '$250'), test: (p) => p < 25000 },
    { value: '250-500', label: (priceLabels[0] || '$250') + ' to ' + (priceLabels[1] || '$500'), test: (p) => p >= 25000 && p < 50000 },
    { value: '500-1000', label: (priceLabels[1] || '$500') + ' to ' + (priceLabels[2] || '$1,000'), test: (p) => p >= 50000 && p < 100000 },
    { value: '1000-2000', label: (priceLabels[2] || '$1,000') + ' to ' + (priceLabels[3] || '$2,000'), test: (p) => p >= 100000 && p < 200000 },
    { value: '2000up', label: (priceLabels[3] || '$2,000') + ' and up', test: (p) => p >= 200000 },
  ];

  const FACETS = {
    colour: { label: 'Colour', kind: 'swatch' },
    vendor: { label: 'Brand', kind: 'list' },
    movement: { label: 'Movement', kind: 'list' },
    style: { label: 'Style', kind: 'chips' },
    size: { label: 'Case size', kind: 'band', bands: SIZE_BANDS },
    price: { label: 'Price', kind: 'band', bands: PRICE_BANDS },
    availability: { label: 'Availability', kind: 'list' },
  };
  const KEYS = Object.keys(FACETS);

  const state = {};
  KEYS.forEach((k) => { state[k] = new Set(); });
  state.q = '';
  state.sort = 'featured';

  let items = [];

  /* ---------- reading a card ---------- */
  function readItem(el) {
    return {
      el,
      index: Number(el.dataset.index || 0),
      family: el.dataset.family || '',
      vendor: el.dataset.vendor || '',
      colour: el.dataset.colour || '',
      movement: el.dataset.movement || '',
      style: el.dataset.style || '',
      availability: el.dataset.availability || '',
      size: parseFloat(el.dataset.size || '0') || 0,
      price: parseInt(el.dataset.price || '0', 10) || 0,
      created: parseInt(el.dataset.created || '0', 10) || 0,
      search: el.dataset.search || '',
    };
  }

  function bandOf(bands, n) {
    const hit = bands.find((b) => b.test(n));
    return hit ? hit.value : '';
  }
  function valueOf(item, key) {
    if (key === 'size') return bandOf(SIZE_BANDS, item.size);
    if (key === 'price') return bandOf(PRICE_BANDS, item.price);
    return item[key] || '';
  }

  /* ---------- facet rail ---------- */
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* A facet's number is the number of CARDS ticking it produces, not the number
     of products behind them. Blue used to read 40 because forty products are
     blue, then rendered 39 cards because two of them are one colourway family.
     Counting distinct families per value makes the number beside a facet exactly
     what the grid gives you, which is the only number a shopper can check. */
  function orderedValues(key) {
    const present = new Map();
    items.forEach((it) => {
      const v = valueOf(it, key);
      if (!v) return;
      let families = present.get(v);
      if (!families) { families = new Set(); present.set(v, families); }
      families.add(it.family || 'idx:' + it.index);
    });
    let order;
    if (key === 'colour') order = COLOURS.map((c) => c[0]);
    else if (key === 'movement') order = MOVEMENTS;
    else if (key === 'style') order = STYLES;
    else if (key === 'availability') order = AVAILABILITY;
    else if (FACETS[key].kind === 'band') order = FACETS[key].bands.map((b) => b.value);
    else order = Array.from(present.keys()).sort((a, b) => a.localeCompare(b));

    const known = order.filter((v) => present.has(v));
    const extra = Array.from(present.keys()).filter((v) => !order.includes(v)).sort((a, b) => a.localeCompare(b));
    return known.concat(extra).map((v) => [v, present.get(v).size]);
  }

  function labelFor(key, value) {
    if (FACETS[key].kind === 'band') {
      const b = FACETS[key].bands.find((x) => x.value === value);
      return b ? b.label : value;
    }
    if (key === 'availability') return AVAILABILITY_LABELS[value] || value;
    return value;
  }

  function buildFacets() {
    KEYS.forEach((key) => {
      const box = $(`[data-facet="${key}"]`);
      const group = $(`[data-group="${key}"]`);
      if (!box || !group) return;
      const values = orderedValues(key);

      /* Nothing to choose between is not a filter. A brand page drops the brand
         facet this way, and a collection with one availability state drops that. */
      if (values.length < 2) {
        group.remove();
        return;
      }
      group.hidden = false;

      if (FACETS[key].kind === 'chips') {
        box.innerHTML = values
          .map(([v, n]) => `<button type="button" class="jhs-chipbtn" data-value="${esc(v)}">${esc(v)}<small>${n}</small></button>`)
          .join('');
      } else if (FACETS[key].kind === 'swatch') {
        box.innerHTML = values
          .map(([v, n]) => {
            const hex = (COLOURS.find((c) => c[0] === v) || [])[1];
            const dot = hex
              ? `<i class="jhs-cdot" style="--sw: ${esc(hex)};"></i>`
              : '<i class="jhs-cdot jhs-cdot--multi"></i>';
            return `<label class="jhs-check"><input type="checkbox" value="${esc(v)}">${dot}<span>${esc(v)}</span><small>${n}</small></label>`;
          })
          .join('');
      } else {
        box.innerHTML = values
          .map(([v, n]) => `<label class="jhs-check"><input type="checkbox" value="${esc(v)}"><span>${esc(labelFor(key, v))}</span><small>${n}</small></label>`)
          .join('');
      }
    });
  }

  /* ---------- filtering, sorting, collapsing ---------- */
  function matches(item) {
    for (const key of KEYS) {
      const chosen = state[key];
      if (chosen.size && !chosen.has(valueOf(item, key))) return false;
    }
    if (state.q && !item.search.includes(state.q.toLowerCase())) return false;
    return true;
  }

  /* Default ("Featured") order ranks by availability, best first. Ties fall back
     to catalogue index, so the order inside each group is unchanged. Unknown or
     blank values rank last. */
  function availRank(it) {
    const i = AVAILABILITY.indexOf(it.availability);
    return i === -1 ? AVAILABILITY.length : i;
  }

  function sortList(list) {
    const by = state.sort;
    return list.slice().sort((a, b) => {
      if (by === 'price-asc') return a.price - b.price || a.index - b.index;
      if (by === 'price-desc') return b.price - a.price || a.index - b.index;
      if (by === 'new') return b.created - a.created || a.index - b.index;
      return availRank(a) - availRank(b) || a.index - b.index;
    });
  }

  /* One card per colourway family: the first member left standing in the current
     order. Collapse runs AFTER sortList, so under the default sort the leading
     member is the best-available one (In stock before Pre-order, etc.) and its
     card, with its own swatch selected, represents the family. Under "price, low to high" that is the cheapest colourway, which is
     the one the shopper asked to see. */
  function collapse(list) {
    const seen = new Set();
    return list.filter((it) => {
      if (!it.family) return true;
      if (seen.has(it.family)) return false;
      seen.add(it.family);
      return true;
    });
  }

  function totalModels() {
    const seen = new Set();
    let n = 0;
    items.forEach((it) => {
      if (!it.family) { n += 1; return; }
      if (seen.has(it.family)) return;
      seen.add(it.family);
      n += 1;
    });
    return n;
  }

  /* When a colour is being filtered on, show the colourway that matched rather
     than whichever sibling happens to lead the family. The swatches already
     carry their sibling's data; this reuses assets/jhs-card.js's own swap. */
  function alignSwatch(item) {
    if (!state.colour.size) return;
    const card = item.el.querySelector('[data-jhs-card]');
    if (!card) return;
    if (state.colour.has(item.colour)) return;
    const hit = Array.from(card.querySelectorAll('[data-jhs-sw]')).find((sw) =>
      state.colour.has(sw.dataset.colourGroup)
    );
    if (hit && !hit.classList.contains('is-on')) hit.click();
  }

  function render() {
    const filtered = items.filter(matches);
    const list = collapse(sortList(filtered));
    const shown = new Set(list.map((it) => it.el));

    items.forEach((it) => { it.el.hidden = !shown.has(it.el); });
    list.forEach((it) => { grid.appendChild(it.el); alignSwatch(it); });

    const empty = $('[data-empty]');
    if (empty) empty.hidden = list.length > 0 || items.length === 0;

    /* applied chips */
    const applied = [];
    KEYS.forEach((key) => {
      state[key].forEach((v) => applied.push([key, v, labelFor(key, v)]));
    });
    if (state.q) applied.push(['q', '', `"${state.q}"`]);

    /* One number, and it is cards. "168 models · 229 watches" was our internal
       distinction leaking onto the page: 229 is an inventory fact, not a
       browsing fact, and a shopper cannot verify it against anything on screen.
       The page now counts what it shows and calls them watches. */
    const all = totalModels();
    const shownCards = list.length;
    const countEl = $('[data-count]');
    if (countEl) {
      let text;
      /* Section setting "Show watch count" off: nothing until a filter is on. */
      const filteredOnly = !!countEl.closest('[data-count-filtered-only]');
      if (applied.length === 0) {
        text = filteredOnly ? '' : `${all} ${all === 1 ? 'watch' : 'watches'}`;
      } else if (shownCards === 0) {
        text = 'No watches match';
      } else {
        text = `Showing ${shownCards} of ${all}`;
      }
      /* Never quietly show fewer watches than the catalogue holds. */
      if (!catalogueComplete) text = (text ? text + ', ' : '') + 'some did not load';
      countEl.textContent = text;
    }

    const showLabel = $('[data-show-label]');
    /* The drawer's apply button counts the same thing the grid does. */
    if (showLabel) showLabel.textContent = `Show ${list.length} ${list.length === 1 ? 'watch' : 'watches'}`;

    const appliedBox = $('[data-applied]');
    if (appliedBox) {
      appliedBox.innerHTML = applied
        .map(
          ([k, v, label]) =>
            `<button type="button" class="jhs-applied" data-remove="${esc(k)}" data-remove-value="${esc(v)}">${esc(label)}<svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M1 1l8 8M9 1L1 9"/></svg></button>`
        )
        .join('');
    }
    const fc = $('[data-filter-count]');
    if (fc) { fc.textContent = String(applied.length); fc.hidden = applied.length === 0; }

    KEYS.forEach((key) => {
      const el = $(`[data-val="${key}"]`);
      if (!el) return;
      const set = state[key];
      const text = set.size === 0 ? 'Any' : set.size === 1 ? labelFor(key, Array.from(set)[0]) : `${set.size} selected`;
      el.textContent = text;
      el.classList.toggle('is-set', set.size > 0);
    });

    $$('.jhs-chipbtn').forEach((b) => {
      const key = b.closest('[data-facet]').dataset.facet;
      b.classList.toggle('is-on', state[key].has(b.dataset.value));
    });
    $$('[data-facet] input[type="checkbox"]').forEach((i) => {
      const key = i.closest('[data-facet]').dataset.facet;
      i.checked = state[key].has(i.value);
    });

    updateBrandStats(filtered);
    syncUrl();
  }

  /* ---------- brand header tiles ---------- */
  function updateBrandStats(filtered) {
    const header = document.querySelector('.jhs-brand-header');
    if (!header) return;
    const set = (name, value) => {
      const b = header.querySelector(`[data-jhs-stat="${name}"]`);
      const wrap = header.querySelector(`[data-jhs-stat-wrap="${name}"]`);
      if (!b || !wrap) return;
      if (value === null || value === '') { wrap.hidden = true; return; }
      b.textContent = value;
      wrap.hidden = false;
    };
    /* Watches = products that are cards (grid-hidden strap options are not in
       items). The server value stands until the whole catalogue is in, so a
       failed page can never shrink the number. */
    if (catalogueComplete) set('watches', String(items.length));
    set('models', String(totalModels()));
    const inStock = items.filter((it) => it.availability === 'In stock').length;
    set('instock', inStock > 0 ? String(inStock) : null);
    const cheapest = items.slice().sort((a, b) => a.price - b.price)[0];
    const priceEl = cheapest && cheapest.el.querySelector('[data-jhs-card-price]');
    set('from', priceEl ? priceEl.textContent.trim() : null);
    void filtered;
  }

  /* ---------- URL state ---------- */
  const URL_KEYS = { colour: 'colour', vendor: 'brand', movement: 'movement', style: 'style', size: 'size', price: 'price', availability: 'availability' };

  function readUrl() {
    const qs = new URLSearchParams(location.search);
    KEYS.forEach((key) => {
      const raw = qs.get(URL_KEYS[key]);
      if (!raw) return;
      const wanted = raw.split(',').map((s) => s.trim()).filter(Boolean);
      const known = orderedValues(key).map((v) => v[0]);
      wanted.forEach((w) => {
        /* accept the value itself or its handle, so ?brand=d1-milano works as
           well as ?brand=D1%20Milano */
        const hit = known.find((v) => v === w || slug(v) === slug(w));
        if (hit) state[key].add(hit);
      });
    });
    const q = qs.get('q');
    if (q) {
      state.q = q;
      const input = $('[data-search]');
      if (input) input.value = q;
    }
    const sort = qs.get('sort');
    if (sort && ['featured', 'price-asc', 'price-desc', 'new'].includes(sort)) {
      state.sort = sort;
      const sel = $('[data-sort]');
      if (sel) sel.value = sort;
    }
  }

  function slug(s) {
    return String(s).toLowerCase().replace(/&/g, ' ').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function syncUrl() {
    const qs = new URLSearchParams(location.search);
    KEYS.forEach((key) => {
      const set = state[key];
      if (set.size) qs.set(URL_KEYS[key], Array.from(set).map(slug).join(','));
      else qs.delete(URL_KEYS[key]);
    });
    if (state.q) qs.set('q', state.q); else qs.delete('q');
    if (state.sort !== 'featured') qs.set('sort', state.sort); else qs.delete('sort');
    qs.delete('page');
    const next = location.pathname + (qs.toString() ? '?' + qs.toString() : '');
    if (next !== location.pathname + location.search) history.replaceState(null, '', next);
  }

  /* ---------- drawer ---------- */
  function openDrawer(open) {
    const rail = $('[data-filters]');
    const scrim = $('.jhs-drawer-scrim');
    if (!rail) return;
    if (open && matchMedia('(max-width: 1023px)').matches) {
      $$('.jhs-fgroup', rail).forEach((d) => { d.open = false; });
    }
    rail.classList.toggle('is-open', open);
    if (scrim) scrim.hidden = !open;
    document.body.classList.toggle('jhs-drawer-open', open);
  }

  function clearAll() {
    KEYS.forEach((k) => state[k].clear());
    state.q = '';
    const input = $('[data-search]');
    if (input) input.value = '';
    render();
  }

  /* ---------- wiring ---------- */
  root.addEventListener('click', (event) => {
    const chip = event.target.closest('.jhs-chipbtn');
    if (chip && chip.closest('[data-facet]')) {
      const key = chip.closest('[data-facet]').dataset.facet;
      state[key].has(chip.dataset.value) ? state[key].delete(chip.dataset.value) : state[key].add(chip.dataset.value);
      render();
      return;
    }
    const remove = event.target.closest('[data-remove]');
    if (remove) {
      const key = remove.dataset.remove;
      if (key === 'q') { state.q = ''; const i = $('[data-search]'); if (i) i.value = ''; }
      else if (state[key]) state[key].delete(remove.dataset.removeValue);
      render();
      return;
    }
    if (event.target.closest('[data-clear]')) { clearAll(); return; }
    if (event.target.closest('[data-open-drawer]')) { openDrawer(true); return; }
    if (event.target.closest('[data-close-drawer]')) { openDrawer(false); }
  });

  root.addEventListener('change', (event) => {
    const input = event.target;
    if (input.matches('[data-facet] input[type="checkbox"]')) {
      const key = input.closest('[data-facet]').dataset.facet;
      input.checked ? state[key].add(input.value) : state[key].delete(input.value);
      render();
    } else if (input.matches('[data-sort]')) {
      state.sort = input.value;
      render();
    }
  });

  let searchTimer = null;
  root.addEventListener('input', (event) => {
    if (!event.target.matches('[data-search]')) return;
    clearTimeout(searchTimer);
    const value = event.target.value.trim();
    searchTimer = setTimeout(() => { state.q = value; render(); }, 160);
  });

  /* ---------- hydrate the rest of the catalog ---------- */
  async function hydrate() {
    const pages = Number(root.dataset.pages || 1);
    if (pages <= 1) return;
    const sectionId = root.dataset.sectionId;
    const base = root.dataset.collectionUrl || location.pathname;
    const carry = new URLSearchParams(location.search);

    /* The page the visitor actually landed on is already in the DOM. This used to
       start at page 2 regardless, so anyone arriving on /collections/all?page=3
       got page 3 twice and never got page 1 at all: 229 cards in the DOM but only
       179 distinct products, and the family collapse quietly swallowed the
       duplicates so it looked like the grid was just repeating itself. Skip the
       rendered page, and key every card on its catalogue index so a retry or a
       double response cannot put the same product in twice. */
    const current = Number(root.dataset.page || 1);
    const seen = new Set($$('[data-item]', grid).map((el) => el.dataset.index));

    /* Fetch every other page at once. These used to run one after another, so on
       a fast connection the catalogue took about two seconds to assemble and on a
       real one considerably longer. The pager is hidden for that whole window
       (see retirePager), and the longer the window the longer the grid is short
       of products, so the requests go in parallel and the window collapses to the
       slowest single page rather than the sum of all four. */
    const wanted = [];
    for (let page = 1; page <= pages; page += 1) {
      if (page !== current) wanted.push(page);
    }

    const results = await Promise.all(
      wanted.map(async (page) => {
        const url = new URL(base, location.origin);
        /* keep preview_theme_id and friends so this works on an unpublished theme */
        carry.forEach((v, k) => { if (k !== 'page' && k !== 'section_id') url.searchParams.set(k, v); });
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', sectionId);
        try {
          const res = await fetch(url.toString());
          if (!res.ok) return null;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const pageRoot = doc.querySelector('[data-jhs-collection]');
          /* No root means this is not our section's markup: count it as failed, so
             its hidden products are never subtracted from the expected total. */
          if (!pageRoot) return null;
          const hidden = Number(pageRoot.dataset.pageHidden || 0) || 0;
          return { page, hidden, nodes: Array.from(doc.querySelectorAll('[data-item]')) };
        } catch (err) {
          return null;
        }
      })
    );

    /* Append in page order, not completion order, so the DOM still reads as the
       catalogue does. The sort in render() does not depend on this, but a
       predictable DOM is easier to reason about. */
    const frag = document.createDocumentFragment();
    results
      .filter(Boolean)
      .sort((a, b) => a.page - b.page)
      .forEach(({ hidden, nodes }) => {
        totalProducts -= hidden;
        nodes.forEach((node) => {
          const key = node.getAttribute('data-index');
          if (seen.has(key)) return;
          seen.add(key);
          frag.appendChild(document.importNode(node, true));
        });
      });
    grid.appendChild(frag);
  }

  /* The pager and the filtered grid are two different modes and they disagree
     about what "168 models" means, so a visitor must never be in both. JS takes
     the grid, so the pager goes the moment JS runs, not when hydration finishes.
     It used to be hidden only at the end of hydration, which left it visible and
     clickable for the whole load: measured at about two seconds on a fast
     connection from a cold load, and longer on a real one. Clicking Next in that
     window landed the visitor on a server-rendered page 2, which is exactly the
     "Page 2 of 5" the client photographed.

     It comes back if hydration could not deliver the whole catalogue, because a
     visitor holding 50 of 229 products needs a way through them, and the count
     line says the totals are short alongside it. */
  function setPagerVisible(visible) {
    const nav = $('[data-pagination]');
    if (nav) nav.hidden = !visible;
  }

  function collect() {
    items = $$('[data-item]', grid).map(readItem);
  }

  function boot() {
    collect();
    buildFacets();
    readUrl();
    const rail = $('[data-filters]');
    if (rail) rail.hidden = false;
    render();
  }

  /* The loader must never fail quietly. If the DOM holds fewer distinct products
     than the catalogue does, the visitor is looking at an incomplete grid, so
     the pager comes back as their way through to the rest, the count line says
     so, and we get told about it in the console. */
  function checkCatalogueComplete() {
    const distinct = new Set($$('[data-item]', grid).map((el) => el.getAttribute('data-index'))).size;
    catalogueComplete = distinct >= totalProducts;
    if (!catalogueComplete) {
      setPagerVisible(true);
      /* eslint-disable-next-line no-console */
      console.error(
        `[jhs-collection] catalogue incomplete: ${distinct} distinct products in the DOM, ${totalProducts} expected. Pager restored.`
      );
    }
    return catalogueComplete;
  }

  /* The grid is invisible (data-hydrating, set by the server markup) until the
     final layout is in, so the reflow from 50 server cards to the collapsed,
     sorted catalogue is never seen. Reveal runs on every exit path, plus a hard
     timeout so a slow or failed fetch can never leave the grid hidden. */
  function reveal() {
    if (grid) grid.removeAttribute('data-hydrating');
  }
  setTimeout(reveal, 3000);

  collect();
  setPagerVisible(false);
  hydrate()
    .then(() => { checkCatalogueComplete(); boot(); })
    .catch(() => { checkCatalogueComplete(); boot(); })
    .finally(reveal);
})();
